import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { dequal } from "dequal";
import type { History, Transition } from "history";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Path } from "react-hook-form";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { UNSAFE_NavigationContext, useNavigate } from "react-router-dom";

import {
  ActivityCategory,
  ContentType,
  GetActivityTypesQuery,
  GetQuestionTypesQuery,
  UpsertDraftActivityMutation,
  UpsertDraftActivityMutationVariables,
} from "@/graphql/graphql";
import { AppPageNoMaxWidth } from "@/layouts";
import {
  GET_QUESTION_TYPES_QUERY,
  UPSERT_DRAFT_ACTIVITY_MUTATION,
} from "@/pages/ActivityLibrary/queries";
import { GET_ACTIVITY_TYPES_QUERY } from "@/pages/UserDetails/components/CallPlans/CreateCallPlanModal/ActivitiesTable/ActivitiesTable.queries";
import { compareActivityTypes } from "@/utils/activities";
import { ACTIVITY_CATEGORIES, ACTIVITY_DIFFICULTIES } from "@/utils/consts";
import { formatEnumLabel, graphqlClientErrorMessage } from "@/utils/format";

import * as sx from "./ActivityCreatePage.styles";
import {
  type ActivityContentBlockProps,
  type ActivityCreateSnackbarsProps,
  type ActivityFormValues,
  type ActivityMetadataCardProps,
  type ActivitySubmitAction,
  type ActivityTypeKeysPickerProps,
  type PendingUnsavedInterrupt,
  type QuestionExpectedAnswersProps,
} from "./ActivityCreatePage.types";
import {
  buildDraftActivityJSON,
  buildFormValuesFromDraftActivityJSON,
  emptyActivityFormValues,
  emptyBlock,
  responseTimeOptions,
  validateTitleBySubmitAction,
} from "./ActivityCreatePage.utils";
import { DraftActivitesModal } from "./DraftActivitesModal";

const ACTIVITY_CREATE_FORM_ID = "activity-create-form";

/** MUI outlined labels may not shrink after RHF `reset` / programmatic value updates; tie shrink to the watched value. */
const shouldShrinkOutlinedLabel = (value: unknown) => value != null && String(value).length > 0;

/**
 * Multi-select activity type keys from API data. Uses early returns instead of nested ternaries.
 */
const ActivityTypeKeysPicker: React.FC<ActivityTypeKeysPickerProps> = ({
  value,
  onChange,
  sortedActivityTypes,
  loading,
  hasError,
}) => {
  const selectedKeys = Array.isArray(value) ? value : value ? [value] : [];

  if (hasError) {
    return (
      <Alert severity="error" sx={sx.activityCreateAlertInlineSx}>
        Could not load activity types. Try again later.
      </Alert>
    );
  }

  if (loading && sortedActivityTypes.length === 0) {
    return (
      <Box sx={sx.activityCreateActivityTypesLoadingWrapSx}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (sortedActivityTypes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No activity types available.
      </Typography>
    );
  }

  return (
    <Box sx={sx.activityCreateActivityTypeGridSx}>
      {sortedActivityTypes.map((activityType) => {
        const active = selectedKeys.includes(activityType.activityTypeKey);
        return (
          <Button
            key={activityType.activityTypeKey}
            onClick={() => {
              if (active) {
                onChange(selectedKeys.filter((item) => item !== activityType.activityTypeKey));
                return;
              }
              onChange([...selectedKeys, activityType.activityTypeKey]);
            }}
            sx={sx.activityCreateActivityTypeButtonSx(active)}
          >
            <Box sx={sx.activityCreateCheckboxBoxSx(active)}>
              {active && <CheckIcon sx={sx.activityCreateActivityTypeCheckIconSx} />}
            </Box>
            <Typography variant="body2" sx={sx.activityCreateActivityTypeRowLabelSx}>
              {activityType.activityTypeLabel}
            </Typography>
          </Button>
        );
      })}
    </Box>
  );
};

const QuestionExpectedAnswers: React.FC<QuestionExpectedAnswersProps> = ({
  control,
  register,
  getValues,
  setValue,
  blockIndex,
}) => {
  const pathName = `contents.${blockIndex}.expectedAnswers` as Path<ActivityFormValues>;
  const answers = (useWatch({ control, name: pathName }) as string[] | undefined) ?? [];

  const addAnswer = () => {
    const current = (getValues(pathName) as string[] | undefined) ?? [];
    setValue(pathName, [...current, ""], { shouldDirty: true });
  };

  const removeAnswer = (answerIndex: number) => {
    const current = (getValues(pathName) as string[] | undefined) ?? [];
    if (current.length <= 1) {
      return;
    }
    setValue(
      pathName,
      current.filter((_, index) => index !== answerIndex),
      {
        shouldDirty: true,
      }
    );
  };

  return (
    <Box>
      <Typography sx={sx.activityCreateLabelSx}>
        Expected Answer(s) (or examples of answers)
      </Typography>
      <Stack spacing={1}>
        {answers.map((_, answerIndex) => (
          <Stack
            direction="row"
            key={`${blockIndex}-answer-${answerIndex}`}
            spacing={1}
            alignItems="center"
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Expected answer"
              {...register(`contents.${blockIndex}.expectedAnswers.${answerIndex}`)}
            />
            {answers.length > 1 && (
              <IconButton
                size="small"
                aria-label="Remove answer"
                onClick={() => removeAnswer(answerIndex)}
                sx={sx.activityCreateExpectedAnswerRemoveIconSx}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>
      <Button
        type="button"
        variant="text"
        size="small"
        onClick={addAnswer}
        sx={sx.activityCreateAddAnotherAnswerButtonSx}
      >
        + Add another answer
      </Button>
    </Box>
  );
};

const ActivityCreatePageHeader: React.FC<{
  onBackToActivities: () => void;
  onOpenLoadDraft: () => void;
  onSaveDraftClick: () => void;
  onSaveAndPublishClick: () => void;
}> = ({ onBackToActivities, onOpenLoadDraft, onSaveDraftClick, onSaveAndPublishClick }) => (
  <Box sx={sx.activityCreatePageHeaderSx}>
    <Typography component="h1" sx={sx.activityCreatePageTitleSx}>
      Create Activity
    </Typography>
    <Box sx={sx.activityCreatePageActionsSx}>
      <Button
        variant="outlined"
        sx={sx.activityCreateSecondaryButtonSx}
        onClick={onBackToActivities}
      >
        Back to activities
      </Button>
      <Button variant="outlined" sx={sx.activityCreateSecondaryButtonSx} onClick={onOpenLoadDraft}>
        Load draft
      </Button>
      <Button
        variant="contained"
        sx={sx.activityCreatePrimaryButtonSx}
        type="submit"
        form={ACTIVITY_CREATE_FORM_ID}
        onClick={onSaveDraftClick}
      >
        Save draft
      </Button>
      <Button
        type="submit"
        variant="contained"
        sx={sx.activityCreatePrimaryButtonSx}
        form={ACTIVITY_CREATE_FORM_ID}
        onClick={onSaveAndPublishClick}
      >
        Save and publish
      </Button>
    </Box>
  </Box>
);

const ActivityInsertBetweenRow: React.FC<{
  onInsertInstruction: () => void;
  onInsertQuestion: () => void;
}> = ({ onInsertInstruction, onInsertQuestion }) => (
  <Box sx={sx.activityCreateInsertBetweenRowSx}>
    <Button
      size="small"
      startIcon={<AddIcon sx={sx.activityCreateInsertRowIconSx} />}
      sx={sx.activityCreateInsertInstructionButtonSx}
      onClick={onInsertInstruction}
    >
      Instruction
    </Button>
    <Button
      size="small"
      startIcon={<AddIcon sx={sx.activityCreateInsertRowIconSx} />}
      sx={sx.activityCreateInsertQuestionButtonSx}
      onClick={onInsertQuestion}
    >
      Question
    </Button>
  </Box>
);

const ActivityMetadataCard: React.FC<ActivityMetadataCardProps> = ({
  control,
  register,
  errors,
  titleWatch,
  descriptionWatch,
  category,
  sortedActivityTypes,
  activityTypesLoading,
  activityTypesHasError,
  submitActionRef,
}) => (
  <Box sx={sx.activityCreateCardSx}>
    <Typography sx={sx.activityCreateSectionTitleSx}>Activity metadata</Typography>
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label="Title"
        {...register("title", {
          validate: (value) => validateTitleBySubmitAction(value, submitActionRef.current),
        })}
        InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(titleWatch) }}
        error={!!errors.title}
        helperText={errors.title?.message}
        fullWidth
      />

      <Box>
        <Typography sx={sx.activityCreateLabelSx}>Category</Typography>
        <Controller
          control={control}
          name="category"
          render={({ field: { value, onChange } }) => (
            <Box sx={sx.activityCreateFullWidthToggleRowSx}>
              {ACTIVITY_CATEGORIES.map((categoryOption) => {
                const styles = sx.categoryToggleStyles[categoryOption];
                const active = value === categoryOption;
                return (
                  <Button
                    key={categoryOption}
                    onClick={() => onChange(categoryOption)}
                    sx={sx.activityCreateFullWidthToggleButtonSx(
                      active,
                      styles.active,
                      styles.inactive
                    )}
                  >
                    {formatEnumLabel(categoryOption)}
                  </Button>
                );
              })}
            </Box>
          )}
        />
      </Box>

      {category === ActivityCategory.CognitiveExercise && (
        <Box>
          <Typography sx={sx.activityCreateLabelSx}>Activity types</Typography>
          <Controller
            control={control}
            name="activityTypeKeys"
            render={({ field: { value, onChange } }) => (
              <ActivityTypeKeysPicker
                value={value}
                onChange={onChange}
                sortedActivityTypes={sortedActivityTypes}
                loading={activityTypesLoading}
                hasError={activityTypesHasError}
              />
            )}
          />
        </Box>
      )}

      <Box>
        <Typography sx={sx.activityCreateLabelSx}>Difficulty</Typography>
        <Controller
          control={control}
          name="difficulty"
          render={({ field: { value, onChange } }) => (
            <Box sx={sx.activityCreateFullWidthToggleRowSx}>
              {ACTIVITY_DIFFICULTIES.map((difficultyOption) => {
                const styles = sx.difficultyToggleStyles[difficultyOption];
                const active = value === difficultyOption;
                return (
                  <Button
                    key={difficultyOption}
                    onClick={() => onChange(difficultyOption)}
                    sx={sx.activityCreateFullWidthToggleButtonSx(
                      active,
                      styles.active,
                      styles.inactive
                    )}
                  >
                    {formatEnumLabel(difficultyOption)}
                  </Button>
                );
              })}
            </Box>
          )}
        />
      </Box>
      <TextField
        size="small"
        label="Additional description"
        {...register("description")}
        InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(descriptionWatch) }}
        multiline
        minRows={2}
        fullWidth
      />
    </Stack>
  </Box>
);

const ActivityContentBlock: React.FC<ActivityContentBlockProps> = ({
  index,
  block,
  expanded,
  isFirst,
  isLast,
  onToggleCollapsed,
  onMoveUp,
  onMoveDown,
  onRemove,
  onExpandIconClick,
  control,
  register,
  getValues,
  setValue,
  sortedQuestionTypes,
  questionTypesLoading,
  questionTypesHasError,
}) => (
  <Box sx={sx.activityCreateBlockCardSx(expanded, block?.contentType)}>
    <Box
      sx={sx.activityCreateBlockHeaderSx}
      onClick={onToggleCollapsed}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggleCollapsed();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={sx.activityCreateBlockHeaderTitleStackSx}
      >
        <Typography variant="subtitle2" component="span" noWrap>
          {block?.contentType === "QUESTION" ? "Question" : "Instruction"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={sx.activityCreateBlockHeaderPreviewTextSx}
        >
          {block?.text ? block.text.slice(0, 50) : "No content yet"}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <IconButton size="small" disabled={isFirst} onClick={onMoveUp}>
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={isLast} onClick={onMoveDown}>
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onExpandIconClick}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
    </Box>

    {expanded && (
      <Box sx={sx.activityCreateBlockBodyInnerSx}>
        <TextField
          size="small"
          label={block?.contentType === "QUESTION" ? "Question text" : "Instructions text"}
          multiline
          minRows={3}
          fullWidth
          {...register(`contents.${index}.text`)}
          InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(block?.text) }}
        />

        {block?.contentType === "QUESTION" && (
          <>
            <Box sx={sx.activityCreateQuestionSettingsGridSx}>
              <TextField
                select
                size="small"
                label="Question Type"
                {...register(`contents.${index}.questionType`)}
                InputLabelProps={{
                  shrink: shouldShrinkOutlinedLabel(block?.questionType),
                }}
                fullWidth
                disabled={
                  questionTypesHasError ||
                  (questionTypesLoading && sortedQuestionTypes.length === 0)
                }
              >
                {sortedQuestionTypes.map((qt) => (
                  <MenuItem key={qt.questionTypeKey} value={qt.questionTypeKey}>
                    {qt.questionTypeLabel}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                size="small"
                label="Max silence before response (sec)"
                {...register(`contents.${index}.silenceBeforeResponseSec`)}
                InputLabelProps={{
                  shrink: shouldShrinkOutlinedLabel(block?.silenceBeforeResponseSec),
                }}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
            </Box>
            <Typography sx={sx.activityCreateLabelSx}>Max Response Time</Typography>
            <Controller
              control={control}
              name={`contents.${index}.responsePreset`}
              render={({ field: { value, onChange } }) => (
                <Box sx={sx.activityCreateResponsePresetRowSx}>
                  {responseTimeOptions.map((option) => (
                    <Button
                      key={option.key}
                      onClick={() => onChange(option.key)}
                      sx={sx.activityCreateResponsePresetButtonSx(value === option.key)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Box>
              )}
            />

            <Box sx={sx.activityCreateScoringSectionSx}>
              <Controller
                control={control}
                name={`contents.${index}.isScorable`}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    }
                    label="Should be scored?"
                  />
                )}
              />
              {block?.isScorable && (
                <>
                  <TextField
                    size="small"
                    label="Instructions for scoring"
                    placeholder="Provide instructions on how to score this question..."
                    multiline
                    minRows={3}
                    fullWidth
                    {...register(`contents.${index}.scoringNotes`)}
                    InputLabelProps={{
                      shrink: shouldShrinkOutlinedLabel(block?.scoringNotes),
                    }}
                  />
                  <QuestionExpectedAnswers
                    control={control}
                    register={register}
                    getValues={getValues}
                    setValue={setValue}
                    blockIndex={index}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    )}
  </Box>
);

const ActivityEmptyContentPlaceholder: React.FC = () => (
  <Box sx={sx.activityCreateEmptyContentPlaceholderSx}>
    <Typography variant="body2">No content sections yet</Typography>
    <Typography variant="caption" display="block" sx={sx.activityCreateEmptyContentCaptionSx}>
      Use the buttons above to add content sections
    </Typography>
  </Box>
);

const ActivityFormActionsFooter: React.FC<{
  onReset: () => void;
  onSaveDraftClick: () => void;
  onSaveAndPublishClick: () => void;
}> = ({ onReset, onSaveDraftClick, onSaveAndPublishClick }) => (
  <Stack direction="row" spacing={1} justifyContent="flex-end" pb={2}>
    <Button variant="outlined" sx={sx.activityCreateResetFormButtonSx} onClick={onReset}>
      Reset form
    </Button>
    <Button
      type="submit"
      variant="contained"
      sx={sx.activityCreatePrimaryButtonSx}
      onClick={onSaveDraftClick}
    >
      Save draft
    </Button>
    <Button
      type="submit"
      variant="contained"
      sx={sx.activityCreatePrimaryButtonSx}
      onClick={onSaveAndPublishClick}
    >
      Save and publish
    </Button>
  </Stack>
);

const UnsavedChangesDialog: React.FC<{
  open: boolean;
  isSaving: boolean;
  onClose: NonNullable<React.ComponentProps<typeof Dialog>["onClose"]>;
  onSaveDraft: () => void;
  onDiscard: () => void;
}> = ({ open, isSaving, onClose, onSaveDraft, onDiscard }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isSaving}>
    <DialogTitle>Unsaved changes</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        You have unsaved changes, do you want to save your work?
      </Typography>
    </DialogContent>
    <DialogActions sx={sx.dialogActionsSx}>
      <Button variant="outlined" onClick={onDiscard} disabled={isSaving}>
        No
      </Button>
      <Button variant="contained" onClick={onSaveDraft} disabled={isSaving}>
        Save draft
      </Button>
    </DialogActions>
  </Dialog>
);

const ActivityCreateSnackbars: React.FC<ActivityCreateSnackbarsProps> = ({
  showError,
  errorNotice,
  onDismissError,
  onErrorSnackbarExited,
  showSuccess,
  successNotice,
  onDismissSuccess,
}) => (
  <>
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={showError}
      autoHideDuration={5000}
      onClose={onDismissError}
      TransitionProps={{ onExited: onErrorSnackbarExited }}
    >
      <Alert severity="error" onClose={onDismissError}>
        {errorNotice ?? "Could not save draft."}
      </Alert>
    </Snackbar>
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={showSuccess}
      autoHideDuration={4000}
      onClose={onDismissSuccess}
    >
      <Alert severity="success" onClose={onDismissSuccess}>
        {successNotice ?? "Draft saved."}
      </Alert>
    </Snackbar>
  </>
);

export const ActivityCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const navigationContext = useContext(UNSAFE_NavigationContext);

  const [upsertDraftActivity, { error: upsertDraftActivityError }] = useMutation<
    UpsertDraftActivityMutation,
    UpsertDraftActivityMutationVariables
  >(UPSERT_DRAFT_ACTIVITY_MUTATION);

  const {
    data: activityTypesData,
    loading: activityTypesLoading,
    error: activityTypesError,
  } = useQuery<GetActivityTypesQuery>(GET_ACTIVITY_TYPES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const {
    data: questionTypesData,
    loading: questionTypesLoading,
    error: questionTypesError,
  } = useQuery<GetQuestionTypesQuery>(GET_QUESTION_TYPES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const sortedQuestionTypes = useMemo(() => {
    const types = questionTypesData?.questionTypes ?? [];
    return [...types].sort((a, b) => a.questionTypeLabel.localeCompare(b.questionTypeLabel));
  }, [questionTypesData?.questionTypes]);

  const sortedActivityTypes = useMemo(() => {
    const types = activityTypesData?.activityTypes ?? [];
    return [...types].sort(compareActivityTypes);
  }, [activityTypesData?.activityTypes]);

  const defaultFormValues = useMemo(() => emptyActivityFormValues(), []);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ActivityFormValues>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultFormValues,
  });
  const [dirtyBaseline, setDirtyBaseline] = useState<ActivityFormValues>(() => defaultFormValues);
  const watchedValues = useWatch({ control });
  // Can't rely on `isDirty` because it's not updated immediately after a value change.
  const hasUnsavedChanges = !dequal(watchedValues ?? dirtyBaseline, dirtyBaseline);

  const resetWithBaseline = useCallback(
    (next: ActivityFormValues) => {
      reset(next);
      setDirtyBaseline(next);
    },
    [reset]
  );
  const { fields, remove, move, insert } = useFieldArray({
    control,
    name: "contents",
  });
  const [collapsedBlocks, setCollapsedBlocks] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [draftActivityId, setDraftActivityId] = useState<number | undefined>();
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [interruptSavePending, setInterruptSavePending] = useState(false);
  const errorSnackbarUserDismissedRef = useRef(false);
  const submitActionRef = useRef<ActivitySubmitAction>("draft");
  const pendingInterruptRef = useRef<PendingUnsavedInterrupt | null>(null);
  /** `history.block` unsubscribe - must run before `tx.retry()` or the retry is blocked again. */
  const navigateBlockUnsubscribeRef = useRef<(() => void) | null>(null);

  const category = useWatch({ control, name: "category" });
  const contents = useWatch({ control, name: "contents" }) ?? [];
  const titleWatch = useWatch({ control, name: "title" });
  const descriptionWatch = useWatch({ control, name: "description" });

  const toggleCollapsed = useCallback(
    (fieldId: string) => {
      setCollapsedBlocks((current) =>
        current.includes(fieldId) ? current.filter((id) => id !== fieldId) : [...current, fieldId]
      );
    },
    [setCollapsedBlocks]
  );

  const reportDraftSaveError = useCallback((notice: string) => {
    errorSnackbarUserDismissedRef.current = false;
    setErrorNotice(notice);
    setShowError(true);
  }, []);

  const reportDraftSaveSuccess = useCallback((notice: string) => {
    setShowError(false);
    setErrorNotice(null);
    setSuccessNotice(notice);
    setShowSuccess(true);
  }, []);

  const persistDraft = useCallback(
    async (data: ActivityFormValues) => {
      try {
        const result = await upsertDraftActivity({
          variables: {
            activityJSON: buildDraftActivityJSON(data),
            id: draftActivityId,
          },
        });
        if (!result.data?.upsertDraftActivity) {
          reportDraftSaveError("Could not save draft.");
          return false;
        }
        setDraftActivityId(result.data.upsertDraftActivity.id);
        return true;
      } catch {
        reportDraftSaveError("Could not save draft.");
        return false;
      }
    },
    [draftActivityId, reportDraftSaveError, upsertDraftActivity]
  );

  const applyDraftFromPicker = useCallback(
    (draft: Extract<PendingUnsavedInterrupt, { kind: "loadDraft" }>["draft"]) => {
      const loaded = buildFormValuesFromDraftActivityJSON(draft.activityJSON);
      resetWithBaseline(loaded);
      setCollapsedBlocks([]);
      setDraftActivityId(draft.id);
      setIsDraftsModalOpen(false);
      reportDraftSaveSuccess("Draft loaded successfully.");
    },
    [reportDraftSaveSuccess, resetWithBaseline]
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      navigateBlockUnsubscribeRef.current = null;
      return;
    }
    const history = navigationContext?.navigator as History | undefined;
    if (!history?.block) {
      return;
    }
    const unblock = history.block((tx) => {
      pendingInterruptRef.current = { kind: "navigate", tx };
      setUnsavedDialogOpen(true);
    });
    navigateBlockUnsubscribeRef.current = unblock;
    return () => {
      navigateBlockUnsubscribeRef.current?.();
      navigateBlockUnsubscribeRef.current = null;
    };
  }, [hasUnsavedChanges, navigationContext]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!upsertDraftActivityError) {
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    if (errorSnackbarUserDismissedRef.current) {
      return;
    }

    setErrorNotice(graphqlClientErrorMessage(upsertDraftActivityError, "Could not save draft."));
    setShowError(true);
  }, [upsertDraftActivityError]);

  const dismissErrorSnackbar = useCallback(() => {
    errorSnackbarUserDismissedRef.current = true;
    setShowError(false);
  }, [errorSnackbarUserDismissedRef, setShowError]);

  const dismissSuccessSnackbar = useCallback(() => {
    setShowSuccess(false);
    setSuccessNotice(null);
  }, [setShowSuccess, setSuccessNotice]);

  const releaseHistoryBlockAndRetryNavigation = useCallback(
    (blockedTransition: Transition) => {
      const unblock = navigateBlockUnsubscribeRef.current;
      navigateBlockUnsubscribeRef.current = null;
      unblock?.();
      blockedTransition.retry();
    },
    [navigateBlockUnsubscribeRef]
  );

  const finishInterruptDiscard = useCallback(() => {
    const pending = pendingInterruptRef.current;
    pendingInterruptRef.current = null;
    setUnsavedDialogOpen(false);
    if (!pending) {
      return;
    }
    if (pending.kind === "navigate") {
      releaseHistoryBlockAndRetryNavigation(pending.tx);
      return;
    }
    if (pending.kind === "reset") {
      resetWithBaseline(emptyActivityFormValues());
      setDraftActivityId(undefined);
      setCollapsedBlocks([]);
      return;
    }
    applyDraftFromPicker(pending.draft);
  }, [applyDraftFromPicker, releaseHistoryBlockAndRetryNavigation, resetWithBaseline]);

  const requestResetForm = useCallback(() => {
    if (!hasUnsavedChanges) {
      resetWithBaseline(emptyActivityFormValues());
      setDraftActivityId(undefined);
      setCollapsedBlocks([]);
      return;
    }
    pendingInterruptRef.current = { kind: "reset" };
    setUnsavedDialogOpen(true);
  }, [hasUnsavedChanges, resetWithBaseline]);

  const requestLoadDraft = useCallback(
    (draft: Extract<PendingUnsavedInterrupt, { kind: "loadDraft" }>["draft"]) => {
      if (draft.id === draftActivityId) {
        setIsDraftsModalOpen(false);
        return;
      }
      if (!hasUnsavedChanges) {
        applyDraftFromPicker(draft);
        return;
      }
      pendingInterruptRef.current = { kind: "loadDraft", draft };
      setUnsavedDialogOpen(true);
    },
    [draftActivityId, hasUnsavedChanges, applyDraftFromPicker]
  );

  const onUnsavedDialogClose: NonNullable<React.ComponentProps<typeof Dialog>["onClose"]> =
    useCallback(
      (_event, reason) => {
        if (interruptSavePending) {
          return;
        }
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          pendingInterruptRef.current = null;
          setUnsavedDialogOpen(false);
        }
      },
      [interruptSavePending, pendingInterruptRef, setUnsavedDialogOpen]
    );

  const onUnsavedDialogSaveDraft = useCallback(() => {
    submitActionRef.current = "draft";
    handleSubmit(async (data) => {
      setInterruptSavePending(true);
      try {
        const ok = await persistDraft(data);
        if (!ok) {
          return;
        }
        const pending = pendingInterruptRef.current;
        pendingInterruptRef.current = null;
        setUnsavedDialogOpen(false);
        if (!pending) {
          return;
        }
        if (pending.kind === "navigate") {
          resetWithBaseline(data);
          releaseHistoryBlockAndRetryNavigation(pending.tx);
          reportDraftSaveSuccess("Draft saved successfully.");
          return;
        }
        if (pending.kind === "reset") {
          resetWithBaseline(emptyActivityFormValues());
          setDraftActivityId(undefined);
          setCollapsedBlocks([]);
          reportDraftSaveSuccess("Draft saved successfully.");
          return;
        }
        resetWithBaseline(buildFormValuesFromDraftActivityJSON(pending.draft.activityJSON));
        setCollapsedBlocks([]);
        setDraftActivityId(pending.draft.id);
        setIsDraftsModalOpen(false);
        reportDraftSaveSuccess("Draft loaded successfully.");
      } finally {
        setInterruptSavePending(false);
      }
    })();
  }, [
    handleSubmit,
    persistDraft,
    releaseHistoryBlockAndRetryNavigation,
    reportDraftSaveSuccess,
    resetWithBaseline,
  ]);

  return (
    <>
      <AppPageNoMaxWidth>
        <Box sx={sx.activityCreatePageWrapSx}>
          <ActivityCreatePageHeader
            onBackToActivities={() => navigate("/activities")}
            onOpenLoadDraft={() => setIsDraftsModalOpen(true)}
            onSaveDraftClick={() => {
              submitActionRef.current = "draft";
            }}
            onSaveAndPublishClick={() => {
              submitActionRef.current = "publish";
            }}
          />

          <Box sx={sx.activityCreateContentSx}>
            <Box
              id={ACTIVITY_CREATE_FORM_ID}
              component="form"
              sx={sx.activityCreateFormSx}
              onSubmit={handleSubmit(async (data) => {
                const submitAction = submitActionRef.current;
                submitActionRef.current = "draft";
                if (submitAction === "publish") {
                  /* save and publish mutation(data) */
                } else {
                  const ok = await persistDraft(data);
                  if (!ok) {
                    return;
                  }
                  reportDraftSaveSuccess("Draft saved successfully.");
                }
              })}
            >
              <ActivityMetadataCard
                control={control}
                register={register}
                errors={errors}
                titleWatch={titleWatch}
                descriptionWatch={descriptionWatch}
                category={category}
                sortedActivityTypes={sortedActivityTypes}
                activityTypesLoading={activityTypesLoading}
                activityTypesHasError={!!activityTypesError}
                submitActionRef={submitActionRef}
              />

              <Box sx={sx.activityCreateCardSx}>
                <Typography sx={sx.activityCreateContentSectionTitleSx}>Content</Typography>

                {questionTypesError && (
                  <Alert severity="error" sx={sx.activityCreateAlertInlineSx}>
                    Could not load question types. Try again later.
                  </Alert>
                )}

                <ActivityInsertBetweenRow
                  onInsertInstruction={() => insert(0, emptyBlock(ContentType.Instruction))}
                  onInsertQuestion={() => insert(0, emptyBlock(ContentType.Question))}
                />

                <Stack spacing={0}>
                  {fields.map((field, index) => {
                    const block = contents[index];
                    const collapsed = collapsedBlocks.includes(field.id);
                    const expanded = !collapsed;
                    const isFirst = index === 0;
                    const isLast = index === fields.length - 1;

                    return (
                      <Box key={field.id}>
                        <ActivityContentBlock
                          index={index}
                          block={block}
                          expanded={expanded}
                          isFirst={isFirst}
                          isLast={isLast}
                          onToggleCollapsed={() => toggleCollapsed(field.id)}
                          onMoveUp={(event) => {
                            event.stopPropagation();
                            move(index, index - 1);
                          }}
                          onMoveDown={(event) => {
                            event.stopPropagation();
                            move(index, index + 1);
                          }}
                          onRemove={(event) => {
                            event.stopPropagation();
                            remove(index);
                          }}
                          onExpandIconClick={(event) => {
                            event.stopPropagation();
                            toggleCollapsed(field.id);
                          }}
                          control={control}
                          register={register}
                          getValues={getValues}
                          setValue={setValue}
                          sortedQuestionTypes={sortedQuestionTypes}
                          questionTypesLoading={questionTypesLoading}
                          questionTypesHasError={!!questionTypesError}
                        />

                        <ActivityInsertBetweenRow
                          onInsertInstruction={() =>
                            insert(index + 1, emptyBlock(ContentType.Instruction))
                          }
                          onInsertQuestion={() =>
                            insert(index + 1, emptyBlock(ContentType.Question))
                          }
                        />
                      </Box>
                    );
                  })}

                  {!fields.length && <ActivityEmptyContentPlaceholder />}
                </Stack>
              </Box>

              <ActivityFormActionsFooter
                onReset={requestResetForm}
                onSaveDraftClick={() => {
                  submitActionRef.current = "draft";
                }}
                onSaveAndPublishClick={() => {
                  submitActionRef.current = "publish";
                }}
              />
            </Box>
          </Box>
        </Box>
      </AppPageNoMaxWidth>
      <DraftActivitesModal
        open={isDraftsModalOpen}
        onClose={() => setIsDraftsModalOpen(false)}
        onLoadDraft={requestLoadDraft}
      />
      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        isSaving={interruptSavePending}
        onClose={onUnsavedDialogClose}
        onSaveDraft={onUnsavedDialogSaveDraft}
        onDiscard={finishInterruptDiscard}
      />
      <ActivityCreateSnackbars
        showError={showError}
        errorNotice={errorNotice}
        onDismissError={dismissErrorSnackbar}
        onErrorSnackbarExited={() => setErrorNotice(null)}
        showSuccess={showSuccess}
        successNotice={successNotice}
        onDismissSuccess={dismissSuccessSnackbar}
      />
    </>
  );
};
